import React from "react";
import { ExternalLink, GuideCard, GuideOrderedList } from "./metaAdsGuideParts";

interface PixelCapiHowToProps {
  isOpen: boolean;
  onToggle: () => void;
  action: React.ReactNode;
  onReachOutToUs?: () => void;
}

export default function PixelCapiHowTo({
  isOpen,
  onToggle,
  action,
  onReachOutToUs,
}: PixelCapiHowToProps) {
  const troubleFooter = (
    <span>
      Having trouble?{" "}
      {onReachOutToUs ? (
        <button
          type="button"
          onClick={onReachOutToUs}
          className="text-[#58F2A9] text-[14px] underline font-normal"
        >
          Reach out to us
        </button>
      ) : (
        <span className="text-[#58F2A9] text-[14px] underline font-normal">
          Reach out to us
        </span>
      )}{" "}
      and we&apos;ll help you fix it
    </span>
  );

  return (
    <div className="relative z-10 flex flex-col gap-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div className="order-1 sm:order-2 flex justify-center sm:justify-end">
          {action}
        </div>
        <div className="order-2 sm:order-1 flex justify-end sm:justify-start">
      <button
  type="button"
  onClick={onToggle}
  className={`text-[14px] flex items-center gap-1 transition hover:opacity-90 ${
    isOpen ? "text-[#58F2A9]" : "text-[#878DA1]"
  }`}
>
  How to do this?
  <svg
    className={`w-5 h-5 transition-transform ${
      isOpen ? "rotate-180 mt-[-2px]" : ""
    }`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
</button>
        </div>
      </div>

      {isOpen && (
        <GuideCard
          title="In Facebook Events Manager:"
          footerRight={troubleFooter}
          titleClassName="font-normal text-white text-[18px] leading-[24px]"
        >
          <div>
            <GuideOrderedList
              items={[
                <div>
                  <p className="font-semibold text-white/80">Access Pixels</p>
                  <GuideOrderedList
                    className="mt-2"
                    items={[
                      <>
                        Go to {"https://"}
                        <span className="sm:hidden">
                          <br />
                        </span>
                        <ExternalLink href="https://business.facebook.com/">
                          business.facebook.com/
                        </ExternalLink>
                      </>,
                      "Navigate to Settings",
                      "Under Data Sources, select Datasets & Pixels",
                    ]}
                  />
                </div>,
                <div>
                  <p className="font-semibold text-white/80">
                    Create or Select a Pixel
                  </p>
                  <GuideOrderedList
                    className="mt-2"
                    items={[
                      <>
                        To create a new Pixel, click blue{" "}
                        <span className="font-semibold text-white/80 text-[14px]">
                          “Add”
                        </span>{" "}
                        button
                      </>,
                      "Name your Pixel and follow the setup instructions",
                      "Whether using an existing Pixel or one you just created, select it and copy the Pixel ID shown below its name",
                    ]}
                  />
                </div>,
                <div>
                  <p className="font-semibold text-white/80">Set Up CAPI Token</p>
                  <GuideOrderedList
                    className="mt-2"
                    items={[
                      <>
                        Go to {"https://"}
                        <span className="sm:hidden">
                          <br />
                        </span>
                        <ExternalLink href="https://eventsmanager.facebook.com/">
                          eventsmanager.facebook.com/
                        </ExternalLink>
                      </>,
                      "Select the Pixel or Dataset you created (ensure the ID matches the one you copied), then click on “Settings” in the Pixel navigation bar",
                      "Scroll to Conversions API section",
                      "Click Generate Access Token (or copy existing token) with Set up with Dataset Quality API selected",
                      "Copy the generated token",
                    ]}
                  />
                </div>,
                <div>
                  <p className="font-semibold text-white/80">
                    Enter Details in Pitchplaylists Meta Ads Builder
                  </p>
                  <GuideOrderedList
                    className="mt-2"
                    items={[
                      "If Facebook was connected previously on Pitchplaylists Meta Ads Builder, reconnect to fetch the latest updates.",
                      "Ensure all 4 fields are filled: Facebook Page, Instagram profile, Ad Account, and Pixel.",
                      "If the CAPI token hasn’t been filled in and saved, you can optionally add it in the provided field after selecting your pixel from the dropdown to improve tracking performance.",
                      "Click the “Save for future use” button to activate accurate tracking for all campaigns’ smart links with the selected Pixel ID.",
                    ]}
                  />
                </div>,
              ]}
            />
            <p className="mt-4 opacity-80">
              And now you&apos;re ready to move on to the most exciting part:
              setting up the ad campaign!
            </p>
          </div>
        </GuideCard>
      )}
    </div>
  );
}
